import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { User, UserCreate, UserUpdate, Role } from "../types";
import { X, Mail, UserIcon, Building, Users, Briefcase } from "lucide-react";
import { apiService } from "../services/api";
import { useAuth } from "../hooks/useAuth";

interface UserFormProps {
  user?: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UserCreate | UserUpdate) => void;
  isLoading?: boolean;
}

export default function UserForm({
  user,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: UserFormProps) {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    businessArea: "",
    division: "",
    role: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const isInitializingRef = useRef(false);

  // Fetch business areas - filtered based on current user's role
  const { data: businessAreasRawResponse, isLoading: loadingBusinessAreas } =
    useQuery("businessAreas", () => apiService.getBusinessAreas(), {
      enabled: isOpen,
    });

  // Process business areas based on current user's role
  const businessAreasResponse = useMemo(() => {
    if (!businessAreasRawResponse) return [];

    const areas = businessAreasRawResponse.data?.success
      ? (businessAreasRawResponse.data.data as string[])
      : [];

    // Filter business areas based on current user's role
    if (currentUser?.roleId === "global_admin") {
      // Global admin can see all business areas
      return areas;
    } else if (
      currentUser?.roleId === "ba_admin" ||
      currentUser?.roleId === "division_admin"
    ) {
      // BA Admin and Division Admin can only see their own business area
      // If they don't have a business area assigned, they can see all areas (for assignment purposes)
      return currentUser.businessArea ? [currentUser.businessArea] : areas;
    }
    return [];
  }, [
    businessAreasRawResponse,
    currentUser?.roleId,
    currentUser?.businessArea,
  ]);

  // Fetch divisions when business area is selected - filtered based on current user's role
  const businessAreaForDivisions =
    currentUser?.roleId === "global_admin"
      ? formData.businessArea
      : currentUser?.businessArea || formData.businessArea;

  const { data: divisionsResponse, isLoading: loadingDivisions } = useQuery(
    ["divisions", businessAreaForDivisions],
    () => apiService.getDivisions(businessAreaForDivisions),
    {
      enabled: isOpen && !!businessAreaForDivisions,
      select: (response) => {
        const divisions = response.data.success
          ? (response.data.data as string[])
          : [];

        // Filter divisions based on current user's role
        if (
          currentUser?.roleId === "global_admin" ||
          currentUser?.roleId === "ba_admin"
        ) {
          // Global admin and BA admin can see all divisions in the selected BA
          return divisions;
        } else if (currentUser?.roleId === "division_admin") {
          // Division admin can only see their own division
          // If they don't have a division assigned, they can see all divisions in their BA
          return currentUser.division ? [currentUser.division] : divisions;
        }
        return divisions; // Default fallback
      },
    },
  );

  // Fetch roles
  const { data: rolesResponse, isLoading: loadingRoles } = useQuery(
    "roles",
    () => apiService.getRoles(),
    {
      enabled: isOpen,
      select: (response) => (response.data.success ? response.data.data : []),
    },
  );

  // Filter roles based on current user's permissions
  const availableRoles =
    rolesResponse?.filter((role: Role) => {
      if (currentUser?.roleId === "global_admin") {
        // Global admin can assign any role
        return true;
      } else if (currentUser?.roleId === "ba_admin") {
        // BA admin can only assign ba_admin, division_admin, and product_owner roles
        return (
          role.id === "ba_admin" ||
          role.id === "division_admin" ||
          role.id === "product_owner"
        );
      } else if (currentUser?.roleId === "division_admin") {
        // Division admin can assign division_admin and product_owner roles
        return role.id === "division_admin" || role.id === "product_owner";
      }
      return false;
    }) || [];

  // Reset form when dialog opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      isInitializingRef.current = true;
      if (user) {
        // Edit mode - populate form with user data
        // For Division admins, auto-populate division with their own division
        const editDivision =
          currentUser?.roleId === "division_admin" && currentUser?.division
            ? currentUser.division
            : user.division || "";

        setFormData({
          email: user.email,
          name: user.name,
          businessArea: user.businessArea || "",
          division: editDivision,
          role: user.roleId || "",
        });
      } else {
        // Create mode - reset form with auto-populated fields for non-global users
        const initialBusinessArea =
          currentUser?.roleId === "global_admin"
            ? ""
            : currentUser?.businessArea || "";
        const initialDivision =
          currentUser?.roleId === "global_admin" ||
          currentUser?.roleId === "ba_admin"
            ? ""
            : currentUser?.division || "";

        setFormData({
          email: "",
          name: "",
          businessArea: initialBusinessArea,
          division: initialDivision,
          role: "",
        });
      }
      setErrors({});
    }
  }, [
    isOpen,
    user,
    currentUser?.roleId,
    currentUser?.businessArea,
    currentUser?.division,
  ]);

  // Auto-select business area for BA/Division admins when business areas are loaded
  // Only auto-populate if the admin HAS a business area assigned
  useEffect(() => {
    if (
      isOpen &&
      !user &&
      currentUser?.roleId !== "global_admin" &&
      currentUser?.businessArea
    ) {
      setFormData((prev) => ({
        ...prev,
        businessArea: currentUser.businessArea || "",
      }));
    }
  }, [isOpen, user, currentUser?.roleId, currentUser?.businessArea]);

  // Auto-select division for Division admins when divisions are loaded
  useEffect(() => {
    if (
      isOpen &&
      !user &&
      divisionsResponse &&
      currentUser?.roleId === "division_admin" &&
      currentUser?.division
    ) {
      setFormData((prev) => ({
        ...prev,
        division: currentUser.division || "",
      }));
    }
  }, [
    isOpen,
    user,
    divisionsResponse,
    currentUser?.roleId,
    currentUser?.division,
  ]);

  // Reset division when business area changes (skip during initial form population)
  useEffect(() => {
    if (isInitializingRef.current) {
      isInitializingRef.current = false;
      return;
    }
    if (formData.division) {
      setFormData((prev) => ({ ...prev, division: "" }));
    }
  }, [formData.businessArea]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (user) {
      // Edit mode - only send changed fields
      const updateData: UserUpdate = {};

      if (formData.email !== user.email) updateData.email = formData.email;
      if (formData.name !== user.name) updateData.name = formData.name;

      // Handle business area - use currentUser's business area if they have one (for BA/Division admins)
      const businessAreaToUse =
        currentUser?.roleId !== "global_admin" && currentUser?.businessArea
          ? currentUser.businessArea
          : formData.businessArea;
      const originalBusinessArea = user.businessArea || "";
      const newBusinessArea = businessAreaToUse.trim();
      if (originalBusinessArea !== newBusinessArea) {
        updateData.businessArea = newBusinessArea;
        // Clearing BA should also clear division
        if (!newBusinessArea) {
          updateData.division = "";
        }
      }

      // Handle division - only use currentUser's division for Division admins, not BA admins
      const divisionToUse =
        currentUser?.roleId === "division_admin" && currentUser?.division
          ? currentUser.division
          : formData.division;
      const originalDivision = user.division || "";
      const newDivision = divisionToUse.trim();
      if (originalDivision !== newDivision) {
        updateData.division = newDivision;
      }

      if (formData.role !== (user.roleId || "")) {
        const trimmed = formData.role.trim();
        if (trimmed) {
          console.log("✅ Adding role to updateData:", trimmed);
          updateData.role = trimmed;
        }
      }

      console.log("updateData after all processing:", updateData);
      console.log("updateData keys:", Object.keys(updateData));
      console.log("updateData is empty?", Object.keys(updateData).length === 0);
      console.log(
        "Final updateData before submission:",
        JSON.stringify(updateData, null, 2),
      );

      onSubmit(updateData);
    } else {
      // Create mode - send all fields
      const createData: UserCreate = {
        email: formData.email,
        name: formData.name,
      };

      // Use currentUser's business area if they have one (for BA/Division admins)
      const businessAreaToUse =
        currentUser?.businessArea || formData.businessArea.trim();
      if (businessAreaToUse) createData.businessArea = businessAreaToUse;

      // Use currentUser's division if they have one (for Division admins)
      const divisionToUse = currentUser?.division || formData.division.trim();
      if (divisionToUse) createData.division = divisionToUse;

      if (formData.role.trim()) createData.role = formData.role.trim();

      onSubmit(createData);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {user ? "Edit User" : "Create New User"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100 ${
                    errors.email
                      ? "border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  placeholder="Enter email address"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100 ${
                    errors.name
                      ? "border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                  placeholder="Enter full name"
                  disabled={isLoading}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Business Area Field */}
            <div>
              <label
                htmlFor="businessArea"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Business Area
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-4 w-4 text-gray-400" />
                </div>
                {currentUser?.roleId !== "global_admin" &&
                currentUser?.businessArea ? (
                  // Show as read-only input for BA/Division admins with assigned business area
                  <input
                    type="text"
                    id="businessArea"
                    value={currentUser.businessArea}
                    readOnly
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                ) : (
                  // Show as dropdown for global admins or unassigned BA/Division admins
                  <select
                    id="businessArea"
                    value={formData.businessArea}
                    onChange={(e) =>
                      handleInputChange("businessArea", e.target.value)
                    }
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-600"
                    disabled={isLoading || loadingBusinessAreas}
                    data-testid="business-area-select"
                    title={`Options: ${businessAreasResponse?.join(", ")}`}
                  >
                    <option value="">Select a business area</option>
                    {businessAreasResponse?.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {currentUser?.roleId === "ba_admin" && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {currentUser.businessArea
                    ? "Business area is automatically set to your assigned area"
                    : "Select a business area (you can assign users to any area)"}
                </p>
              )}
              {currentUser?.roleId === "division_admin" && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {currentUser.businessArea
                    ? "Business area is automatically set to your assigned area"
                    : "Select a business area (you can assign users to any area)"}
                </p>
              )}
            </div>

            {/* Division Field */}
            <div>
              <label
                htmlFor="division"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Division/Business Unit
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  id="division"
                  value={formData.division}
                  onChange={(e) =>
                    handleInputChange("division", e.target.value)
                  }
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-600"
                  disabled={
                    isLoading ||
                    !businessAreaForDivisions ||
                    loadingDivisions ||
                    (currentUser?.roleId === "division_admin" &&
                      !!currentUser?.division)
                  }
                >
                  <option value="">
                    {!businessAreaForDivisions
                      ? "Select a business area first"
                      : loadingDivisions
                        ? "Loading divisions/business units..."
                        : "Select a division/business unit"}
                  </option>
                  {divisionsResponse?.map((division) => (
                    <option key={division} value={division}>
                      {division}
                    </option>
                  ))}
                </select>
              </div>
              {currentUser?.roleId === "division_admin" && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {currentUser.division
                    ? "Division/Business Unit is automatically set to your assigned division/business unit"
                    : "Select a division/business unit (you can assign users to any division/business unit in the selected business area)"}
                </p>
              )}
            </div>

            {/* Role Field */}
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Role
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  disabled={isLoading || loadingRoles}
                >
                  <option value="">Select a role</option>
                  {availableRoles.map((role: Role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              {loadingRoles && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Loading roles...
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Role determines user permissions (e.g., Global Admin, Business
                Area Admin, etc.)
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : user ? "Update User" : "Create User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
